/**
 * usePackageSaveFlow — the drawer's Save Config interceptor for
 * package-attributed campaigns. When the session changed package-owned
 * content, Save opens PackageSaveDialog instead of writing straight away;
 * each choice ends by calling the drawer's own save with the right
 * packageId (everyone → same id, campaign-only → same id + edited badge,
 * fork → the new package's id).
 * For Media Devoted | Created By Ciro Autiero
 */

import { createElement, useState, type ReactNode } from 'react';
import type { FlowOptimizerConfig } from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import { useLibraryPackages } from '../../api/hooks/useLibraryPackages';
import PackageSaveDialog from './PackageSaveDialog';
import { shouldAskPackageSave } from './package-save.util';
import { usePackageSave } from './use-package-save';

export interface PackageSaveFlowArgs {
  persisted: FlowOptimizerConfig;
  draft: FlowOptimizerConfig;
  /** The drawer's real save — packageId lands in the config PUT;
   *  packageApplied=true refreshes the applied stamp (clean badge). */
  save: (packageId: string | null, packageApplied?: boolean) => void;
}

export function usePackageSaveFlow({ persisted, draft, save }: PackageSaveFlowArgs): {
  /** True = the dialog took over this Save click. */
  intercept: () => boolean;
  dialog: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const packages = useLibraryPackages(open);
  const { updateEveryone, fork } = usePackageSave();
  const packageId = draft.packageId ?? '';
  const pkg = (packages.data ?? []).find((p) => p.id === packageId);

  const fail = (title: string) => (error: unknown) =>
    toast.error(title, getApiErrorMessage(error));

  const onUpdateEveryone = () =>
    updateEveryone.mutate(
      { packageId, draft },
      {
        onSuccess: (source) => {
          setOpen(false);
          toast.success(
            'Package updated for everyone',
            `"${source.name}" now carries this campaign's formulas and rules.`,
          );
          save(packageId, true);
        },
        onError: fail('Package update failed'),
      },
    );

  const onFork = (name: string) =>
    fork.mutate(
      { packageId, draft, name },
      {
        onSuccess: (created) => {
          setOpen(false);
          toast.success(
            'New package created',
            `"${created.name}" is in the library — this campaign now follows it.`,
          );
          save(created.id);
        },
        onError: fail('Could not create the package'),
      },
    );

  const dialog = createElement(PackageSaveDialog, {
    open,
    packageName: pkg?.name ?? 'this package',
    campaignCount: pkg?.campaignCount ?? 1,
    busy: updateEveryone.isPending || fork.isPending,
    onUpdateEveryone,
    onCampaignOnly: () => {
      setOpen(false);
      save(packageId);
    },
    onFork,
    onCancel: () => setOpen(false),
  });

  return {
    intercept: () => {
      if (!shouldAskPackageSave(persisted, draft)) return false;
      setOpen(true);
      return true;
    },
    dialog,
  };
}
